import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import {
  Calculator,
  LogOut,
  ArrowLeft,
  Plus,
  Pencil,
  Trash2,
  Loader2,
  Trophy,
  Euro,
  Percent,
  Mail,
  User
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminFixedBonuses = () => {
  const navigate = useNavigate();
  const { user, logout, getAuthHeaders } = useAuth();
  const [loading, setLoading] = useState(true);
  const [bonuses, setBonuses] = useState([]);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingBonus, setEditingBonus] = useState(null);
  const [formData, setFormData] = useState({
    email: "",
    name: "",
    tiers: [{ min_margin: 0, max_margin: null, bonus_value: 0, description: "" }],
    active: true
  });

  useEffect(() => {
    loadBonuses();
  }, []);

  const loadBonuses = async () => {
    try {
      const response = await axios.get(`${API}/fixed-bonuses`, { headers: getAuthHeaders() });
      setBonuses(response.data);
    } catch (error) {
      toast.error("Erro ao carregar prémios fixos");
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate("/");
  };

  const openNewDialog = () => {
    setEditingBonus(null);
    setFormData({
      email: "",
      name: "",
      tiers: [{ min_margin: 0, max_margin: null, bonus_value: 0, description: "" }],
      active: true
    });
    setIsDialogOpen(true);
  };

  const openEditDialog = (bonus) => {
    setEditingBonus(bonus);
    setFormData({
      email: bonus.email,
      name: bonus.name || "",
      tiers: bonus.tiers || [{ min_margin: 0, max_margin: null, bonus_value: 0, description: "" }],
      active: bonus.active !== false
    });
    setIsDialogOpen(true);
  };

  const addTier = () => {
    setFormData({
      ...formData,
      tiers: [...formData.tiers, { min_margin: null, max_margin: null, bonus_value: null, description: "" }]
    });
  };

  const removeTier = (index) => {
    if (formData.tiers.length <= 1) {
      toast.error("Deve existir pelo menos um nível");
      return;
    }
    setFormData({
      ...formData,
      tiers: formData.tiers.filter((_, i) => i !== index)
    });
  };

  const updateTier = (index, field, value) => {
    const newTiers = [...formData.tiers];
    if (field === "description") {
      newTiers[index][field] = value;
    } else if (value === "") {
      // Empty stays null (no default 0)
      newTiers[index][field] = null;
    } else {
      const parsed = parseFloat(value);
      newTiers[index][field] = isNaN(parsed) ? null : parsed;
    }
    setFormData({ ...formData, tiers: newTiers });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.email.trim()) {
      toast.error("Email do utilizador é obrigatório");
      return;
    }
    try {
      if (editingBonus) {
        await axios.put(`${API}/fixed-bonuses/${editingBonus.id}`, formData, { headers: getAuthHeaders() });
        toast.success("Prémio fixo atualizado!");
      } else {
        await axios.post(`${API}/fixed-bonuses`, formData, { headers: getAuthHeaders() });
        toast.success("Prémio fixo criado!");
      }
      setIsDialogOpen(false);
      loadBonuses();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erro ao guardar prémio fixo");
    }
  };

  const handleDelete = async (bonusId) => {
    if (!window.confirm("Tem a certeza que deseja eliminar este prémio fixo?")) return;
    try {
      await axios.delete(`${API}/fixed-bonuses/${bonusId}`, { headers: getAuthHeaders() });
      toast.success("Prémio fixo eliminado");
      loadBonuses();
    } catch (error) {
      toast.error("Erro ao eliminar prémio fixo");
    }
  };

  const getTierColor = (value) => {
    if (value === 0) return "bg-gray-50 border-gray-200";
    if (value <= 200) return "bg-blue-50 border-blue-200";
    if (value <= 500) return "bg-brand-50 border-brand-200";
    return "bg-amber-50 border-amber-200";
  };

  return (
    <div className="min-h-screen bg-background" data-testid="admin-fixed-bonuses-page">
      <header className="sticky top-0 z-50 glass border-b border-slate-200/50">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-brand-600 flex items-center justify-center">
              <Calculator className="w-5 h-5 text-white" />
            </div>
            <div>
              <span className="font-manrope font-bold text-lg text-foreground">Evolutt</span>
              <p className="text-xs text-muted-foreground">Prémios Fixos</p>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <span className="text-sm text-muted-foreground">{user?.name}</span>
            <Button variant="ghost" size="sm" onClick={handleLogout} data-testid="logout-btn">
              <LogOut className="w-4 h-4 mr-2" />
              Sair
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-6 py-8">
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-4">
            <Link to="/admin">
              <Button variant="outline" size="sm" data-testid="back-btn">
                <ArrowLeft className="w-4 h-4 mr-2" />
                Voltar
              </Button>
            </Link>
            <div>
              <h1 className="font-manrope font-extrabold text-2xl text-foreground flex items-center gap-2">
                <Trophy className="w-6 h-6 text-amber-500" />
                Prémios Fixos por Utilizador
              </h1>
              <p className="text-muted-foreground">
                Atribuir prémios fixos a utilizadores específicos por email
              </p>
            </div>
          </div>

          <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
            <DialogTrigger asChild>
              <Button
                className="bg-brand-600 text-white hover:bg-brand-700 font-bold"
                onClick={openNewDialog}
                data-testid="add-fixed-bonus-btn"
              >
                <Plus className="w-4 h-4 mr-2" />
                Novo Prémio Fixo
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle className="font-manrope font-bold text-xl">
                  {editingBonus ? "Editar Prémio Fixo" : "Novo Prémio Fixo"}
                </DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-6 mt-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="flex items-center gap-1">
                      <Mail className="w-3.5 h-3.5" />
                      Email do Utilizador
                    </Label>
                    <Input
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      placeholder="utilizador@empresa.pt"
                      required
                      data-testid="bonus-email-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="flex items-center gap-1">
                      <User className="w-3.5 h-3.5" />
                      Nome (referência)
                    </Label>
                    <Input
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      placeholder="Nome do colaborador"
                      data-testid="bonus-name-input"
                    />
                  </div>
                </div>

                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <Label className="font-semibold">Níveis de Prémio (por Margem)</Label>
                    <Button type="button" variant="outline" size="sm" onClick={addTier} data-testid="add-tier-btn">
                      <Plus className="w-4 h-4 mr-1" /> Nível
                    </Button>
                  </div>

                  <div className="p-3 bg-blue-50 rounded-lg border border-blue-200 mb-2">
                    <p className="text-xs text-blue-800">
                      Define o valor fixo do prémio consoante a margem do negócio. O prémio é considerado como custo empresa e refletido na margem final.
                    </p>
                  </div>

                  <div className="space-y-3">
                    {formData.tiers.map((tier, index) => (
                      <div key={index} className={`p-4 rounded-lg border-2 ${getTierColor(tier.bonus_value)}`}>
                        <div className="grid grid-cols-5 gap-3 items-end">
                          <div>
                            <Label className="text-xs text-muted-foreground">Margem Mín. (%)</Label>
                            <div className="relative">
                              <Input
                                type="number"
                                min="0"
                                step="1"
                                className="font-mono pr-8"
                                value={tier.min_margin === null ? "" : tier.min_margin}
                                onChange={(e) => updateTier(index, "min_margin", e.target.value)}
                                placeholder="—"
                              />
                              <Percent className="absolute right-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground" />
                            </div>
                          </div>
                          <div>
                            <Label className="text-xs text-muted-foreground">Margem Máx. (%)</Label>
                            <div className="relative">
                              <Input
                                type="number"
                                min="0"
                                step="1"
                                className="font-mono pr-8"
                                value={tier.max_margin === null ? "" : tier.max_margin}
                                onChange={(e) => updateTier(index, "max_margin", e.target.value)}
                                placeholder="sem limite"
                              />
                              <Percent className="absolute right-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground" />
                            </div>
                          </div>
                          <div>
                            <Label className="text-xs text-muted-foreground">Valor Prémio</Label>
                            <div className="relative">
                              <Input
                                type="number"
                                min="0"
                                step="10"
                                className="font-mono pl-8"
                                value={tier.bonus_value === null ? "" : tier.bonus_value}
                                onChange={(e) => updateTier(index, "bonus_value", e.target.value)}
                                placeholder="0"
                              />
                              <Euro className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground" />
                            </div>
                          </div>
                          <div>
                            <Label className="text-xs text-muted-foreground">Descrição</Label>
                            <Input
                              value={tier.description}
                              onChange={(e) => updateTier(index, "description", e.target.value)}
                              placeholder="Ex: Prémio Base"
                            />
                          </div>
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            className="text-red-500 hover:text-red-700 hover:bg-red-50"
                            onClick={() => removeTier(index)}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                        <p className="mt-2 text-xs text-muted-foreground">
                          Margem {tier.min_margin}%-{tier.max_margin || "sem limite"}%: Prémio fixo de {tier.bonus_value}
                        </p>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="flex justify-end gap-3">
                  <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)}>
                    Cancelar
                  </Button>
                  <Button type="submit" className="bg-primary text-primary-foreground" data-testid="save-fixed-bonus-btn">
                    {editingBonus ? "Guardar Alterações" : "Criar Prémio"}
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        <Card className="bg-white border border-border/60 shadow-sm">
          <CardContent className="p-0">
            {loading ? (
              <div className="flex items-center justify-center h-64">
                <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
              </div>
            ) : bonuses.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-64 text-center">
                <Trophy className="w-12 h-12 text-muted-foreground mb-4" />
                <p className="text-muted-foreground">Nenhum prémio fixo configurado</p>
                <p className="text-sm text-muted-foreground">Atribua prémios fixos a utilizadores por email</p>
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow className="bg-muted/50">
                    <TableHead className="font-semibold">Email</TableHead>
                    <TableHead className="font-semibold">Nome</TableHead>
                    <TableHead className="font-semibold">Níveis</TableHead>
                    <TableHead className="font-semibold">Estado</TableHead>
                    <TableHead className="font-semibold text-right">Ações</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {bonuses.map((bonus) => (
                    <TableRow key={bonus.id} data-testid={`bonus-row-${bonus.id}`}>
                      <TableCell className="font-mono text-sm">{bonus.email}</TableCell>
                      <TableCell>{bonus.name || "—"}</TableCell>
                      <TableCell>
                        <div className="flex flex-wrap gap-1">
                          {(bonus.tiers || []).map((t, i) => (
                            <span key={i} className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-800">
                              {t.min_margin}%-{t.max_margin || "max"}: {t.bonus_value}
                            </span>
                          ))}
                        </div>
                      </TableCell>
                      <TableCell>
                        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${bonus.active !== false ? 'bg-brand-100 text-brand-800' : 'bg-gray-100 text-gray-600'}`}>
                          {bonus.active !== false ? "Ativo" : "Inativo"}
                        </span>
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-1">
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => openEditDialog(bonus)}
                            data-testid={`edit-bonus-${bonus.id}`}
                          >
                            <Pencil className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="text-destructive"
                            onClick={() => handleDelete(bonus.id)}
                            data-testid={`delete-bonus-${bonus.id}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      </main>
    </div>
  );
};

export default AdminFixedBonuses;
